import React from 'react';
import type { FunnelInsightsApiClient } from '../../api/insightsClient';
import {
  buildProviderSourceReportKeys,
  CREATIVE_PROVIDER_SOURCE_REPORT_MAX_KEYS,
  type CreativeDecisionProjection,
  type ProviderSourceReportEnrichmentItem,
} from '../../creativeDecisions';

interface UseCreativeProviderSourceEnrichmentRequest {
  client: Pick<FunnelInsightsApiClient, 'getCreativeProviderSourceEnrichment'>;
  projectId: string;
  rows: readonly CreativeDecisionProjection[];
  /** The caller enables this only for the Creative Decisions view. */
  enabled: boolean;
  /** Changes when the report scope is replaced; load-more alone does not. */
  scopeKey: string;
}

export interface UseCreativeProviderSourceEnrichmentResult {
  byCorrelationKey: Readonly<Record<string, ProviderSourceReportEnrichmentItem | undefined>>;
  loading: boolean;
  error: string | null;
}

/**
 * Optional, lazy provider-source projection. It owns no report rows or
 * metrics: failed batches only remove previews, while successful batches and
 * previously cached source evidence remain usable.
 */
export function useCreativeProviderSourceEnrichment({
  client,
  projectId,
  rows,
  enabled,
  scopeKey,
}: UseCreativeProviderSourceEnrichmentRequest): UseCreativeProviderSourceEnrichmentResult {
  const cacheRef = React.useRef(new Map<string, ProviderSourceReportEnrichmentItem>());
  const activeScopeRef = React.useRef(scopeKey);
  const runRef = React.useRef(0);
  const [byCorrelationKey, setByCorrelationKey] = React.useState<
    Readonly<Record<string, ProviderSourceReportEnrichmentItem>>
  >({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const keys = React.useMemo(() => buildProviderSourceReportKeys(rows), [rows]);
  const keysSignature = JSON.stringify(keys);
  const getCreativeProviderSourceEnrichment = client.getCreativeProviderSourceEnrichment;

  React.useEffect(() => {
    if (activeScopeRef.current === scopeKey) return;
    activeScopeRef.current = scopeKey;
    cacheRef.current.clear();
    setByCorrelationKey({});
    setError(null);
  }, [scopeKey]);

  React.useEffect(() => {
    if (!enabled || !projectId || keys.length === 0) {
      setLoading(false);
      return undefined;
    }
    const missing = keys.filter((key) => !cacheRef.current.has(key.correlationKey));
    if (missing.length === 0) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const runId = ++runRef.current;
    const chunks = chunk(missing, CREATIVE_PROVIDER_SOURCE_REPORT_MAX_KEYS);
    setLoading(true);
    setError(null);
    void Promise.allSettled(chunks.map(async (items) => {
      const response = await getCreativeProviderSourceEnrichment.call(client, {
        projectId,
        items,
        signal: controller.signal,
      });
      if (controller.signal.aborted || runRef.current !== runId) return;
      for (const item of response.data.items) {
        cacheRef.current.set(item.correlationKey, item);
      }
      // Publish each successful batch immediately. A slow or failed sibling
      // batch never hides provider evidence that is already safe to render.
      setByCorrelationKey(Object.fromEntries(cacheRef.current));
    })).then((results) => {
      if (controller.signal.aborted || runRef.current !== runId) return;
      const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (failed) {
        setError(failed.reason instanceof Error ? failed.reason.message : String(failed.reason));
      }
    }).finally(() => {
      if (!controller.signal.aborted && runRef.current === runId) setLoading(false);
    });

    return () => {
      controller.abort();
      if (runRef.current === runId) runRef.current += 1;
    };
    // keysSignature is the deterministic provider request identity. Depending
    // on it avoids restarting a request when a parent recreates equivalent rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCreativeProviderSourceEnrichment, enabled, projectId, scopeKey, keysSignature]);

  // Never paint cached evidence from the previous report scope while the
  // reset effect is waiting to run. Identical deployment keys may legitimately
  // appear in two date/filter scopes with different exact snapshots.
  const scopeMatches = activeScopeRef.current === scopeKey;
  return enabled && scopeMatches
    ? { byCorrelationKey, loading, error }
    : { byCorrelationKey: {}, loading: false, error: null };
}

function chunk<T>(values: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }
  return chunks;
}
